<template>
  <div>
    <div v-if="processed">
      <h1>{{ $t(i18n.changes_pending_label) }}</h1>
      <info-block variant="warning" icon="alert">
        <div v-html="mediaItemChangeAddressResult.htmlContent"></div>
      </info-block>

      <button-group top-spacing="xs">
        <button-fragment type="button" @click="closeModal">
          {{ $t(i18n.confirm_label) }}
        </button-fragment>
      </button-group>
    </div>
    <div v-else>
      <form>
        <AddressInput
            result-label="test"
            margin-top="0"
            :address="formValues.address" />
        <field-error-messages
          :validation-state="$v.formValues.address.streetName"
        />

        <fragment-separator />

        <radio-group-fragment
          key="addressChangeType"
          group-name="radioGroupAddressChangeTypes"
          :disabled="isProcessing"
          :label="$t(i18n.type_change_label)"
          :options="radioGroupAddressChangeTypes"
          v-model="formValues.addressChangeType"
          :is-invalid="isFieldInvalid($v.formValues.addressChangeType)"
        >
          <template #result>
            <field-error-messages
              :validation-state="$v.formValues.addressChangeType"
            />
          </template>
        </radio-group-fragment>

        <radio-group-fragment
          key="gdprShareWithThirdParties"
          group-name="gdprShareWithThirdPartyOptions"
          :disabled="isProcessing"
          :label="$t(i18n.gdpr_share_confirmation_label)"
          :options="gdprShareWithThirdPartyOptions"
          v-model="formValues.shouldCompanyDepartmentsBeNotified"
          :is-invalid="
            isFieldInvalid($v.formValues.shouldCompanyDepartmentsBeNotified)
          "
        >
          <template #result>
            <field-error-messages
              :validation-state="
                $v.formValues.shouldCompanyDepartmentsBeNotified
              "
            /> </template
        ></radio-group-fragment>

        <fragment-separator />

        <date-input-fragment
          key="startDate"
          :disabled="isProcessing"
          :label="$t(i18n.startdate_label)"
          v-model="formValues.startDate"
          :is-invalid="isFieldInvalid($v.formValues.startDate)"
        >
          <template #result>
            <field-error-messages :validation-state="$v.formValues.startDate" />
          </template>
        </date-input-fragment>

        <fragment-separator />

        <text-input-fragment
          key="fullName"
          :disabled="isProcessing"
          :label="$t(i18n.fullname_label)"
          v-model="formValues.fullName"
          :is-invalid="isFieldInvalid($v.formValues.fullName)"
        >
          <template #result>
            <field-error-messages :validation-state="$v.formValues.fullName" />
          </template>
        </text-input-fragment>

        <text-input-fragment
          key="emailAddress"
          :disabled="isProcessing"
          :label="$t(i18n.emailaddress_label)"
          v-model="formValues.emailAddress"
          :is-invalid="isFieldInvalid($v.formValues.emailAddress)"
        >
          <template #result>
            <field-error-messages
              :validation-state="$v.formValues.emailAddress"
            />
          </template>
        </text-input-fragment>

        <text-input-fragment
          key="telephone"
          :disabled="isProcessing"
          :label="$t(i18n.phone_label)"
          v-model="formValues.telephone"
          :is-invalid="isFieldInvalid($v.formValues.telephone)"
        >
          <template #result>
            <field-error-messages :validation-state="$v.formValues.telephone" />
          </template>
        </text-input-fragment>

        <fragment-separator />

        <div v-if="error">
          <p class="error-message">{{ error }}</p>

          <fragment-separator />
        </div>
        
        <button-group direction="right">
          <button-fragment
            :disabled="isProcessing"
            type="button"
            key="submit-button"
            @click="submitCalled()"
          >
            {{ $t(i18n.submit_label) }}
          </button-fragment>
        </button-group>
      </form>
    </div>
  </div>
</template>

<script>
import { Api, Utils } from "@bvm/core";
import {
  FragmentSeparator,
  TextInputFragment,
  RadioGroupFragment,
  DateInputFragment,
  ButtonFragment,
  ButtonGroup,
  InfoBlock
} from "@bvm/forms";
import { required, email } from "vuelidate/lib/validators";
import FieldErrorMessages from "../shared/field-error-messages";
import { changeAddressRequest } from "../../api/change-request-api";
import AddressInput from "../shared/address-input";

const i18nBuilder = new Utils.I18n.I18nBuilder("change_address")
  .add("changes_pending_label")
  .add("confirm_label")
  .add("new_postalcode_label")
  .add("new_housenumber_label")
  .add("type_change_label")
  .add("type_change_postalcode_label")
  .add("type_change_risk_label")
  .add("gdpr_share_confirmation_label")
  .add("gdpr_share_confirmation_yes_label")
  .add("gdpr_share_confirmation_no_label")
  .add("startdate_label")
  .add("fullname_label")
  .add("emailaddress_label")
  .add("phone_label")
  .add("error_label")
  .add("submit_label");

const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
  .add("email")
  .add("required");

export const messageKeys = [
  ...i18nBuilder.getKeys(),
  ...i18nValidationBuilder.getKeys()
];

const mediaTags = {
  CHANGE_ADDRESS_RESULT: "CHANGE_ADDRESS_RESULT"
};

export default {
  name: "change-address-form",
  components: {
    FragmentSeparator,
    TextInputFragment,
    RadioGroupFragment,
    DateInputFragment,
    ButtonFragment,
    ButtonGroup,
    InfoBlock,
    FieldErrorMessages,
    AddressInput
  },
  data() {
    return {
      i18n: i18nBuilder.getData(),
      processed: false,
      isProcessing: false,
      mediaItemChangeAddressResult: "",
      error: "",
      formValues: {
        address: {
          streetName: "",
          houseNumber: "",
          houseNumberAddition: "",
          postalCode: "",
          city: ""
        },
        addressChangeType: "",
        shouldCompanyDepartmentsBeNotified: "",
        startDate: "",
        fullName: "",
        emailAddress: "",
        telephone: ""
      }
    };
  },
  validations() {
    return {
      formValues: {
        address: {
          streetName: { address_required: required }
        },
        addressChangeType: { required },
        shouldCompanyDepartmentsBeNotified: { required },
        startDate: { required },
        fullName: { required },
        emailAddress: { required, email },
        telephone: { required }
      }
    };
  },
  mounted() {
    this.error = "";

    Api.MediaItem.getMediaItem(
      "bvmop basic clean display on",
      "article",
      true,
      mediaTags.CHANGE_ADDRESS_RESULT
    ).then(mediaItem => {
      this.mediaItemChangeAddressResult = mediaItem;
    });
  },
  methods: {
    isFieldInvalid(field) {
      return field.$dirty && field.$invalid;
    },
    sendData() {
      const preparedData = {
        postalCode: this.formValues.address.postalCode,
        houseNumber:
          this.formValues.address.houseNumber +
          this.formValues.address.houseNumberAddition,
        addressType: this.formValues.addressChangeType,
        agreeGdprShareWithThirdParty: this.formValues
          .shouldCompanyDepartmentsBeNotified === 'y',
        startDate: this.formValues.startDate,
        fullName: this.formValues.fullName,
        emailAddress: this.formValues.emailAddress,
        telephone: this.formValues.telephone
      };

      this.error = "";
      this.isProcessing = true;

      changeAddressRequest(preparedData)
        .then(result => {
          if (result.status === 200) {
            this.processed = true;
            this.isProcessing = false;
          }
        })
        .catch(() => {
          this.processed = false;
          this.isProcessing = false;
          this.error = this.$t(this.i18n.error_label);
        });
    },
    submitCalled() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.sendData();
      }
    },
    closeModal() {
      this.$emit("close");
    }
  },
  computed: {
    radioGroupAddressChangeTypes() {
      return {
        POSTAL_ADDRESS: this.$t(this.i18n.type_change_postalcode_label),
        RISK_VISIT_ADDRESS: this.$t(this.i18n.type_change_risk_label)
      };
    },
    gdprShareWithThirdPartyOptions() {
      return {
        y: this.$t(this.i18n.gdpr_share_confirmation_yes_label),
        n: this.$t(this.i18n.gdpr_share_confirmation_no_label)
      };
    }
  }
};
</script>
